import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faDownload, faCalendarAlt, faRupeeSign, faChartPie, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import CommonTable from '../../table/ComonTable';
import { amenityCommonApi } from '../amenityCommonApi';

const AmenityReports = ({ siteId }) => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Filter State
    const [filters, setFilters] = useState({
        fromDate: '',
        toDate: '',
        bookingStatus: 'All'
    });

    useEffect(() => {
        // Fetch initial month data on mount
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
        
        setFilters(prev => ({ ...prev, fromDate: firstDay, toDate: lastDay }));
        fetchReports(firstDay, lastDay, 'All');
    }, [siteId]);

    const fetchReports = async (from, to, status) => {
        if (!siteId) return;
        setLoading(true);
        try {
            const apiFilters = { siteId };
            if (from) apiFilters.fromDate = from;
            if (to) apiFilters.toDate = to;
            if (status !== 'All') apiFilters.bookingStatus = status;

            const res = await amenityCommonApi.getBookingReport(apiFilters);
            if ((res.data?.isSuccess || res.data?.IsSuccess) && Array.isArray(res.data.data)) {
                const mappedData = res.data.data.map(item => ({
                    ...item,
                    bookingId: item.BookingId ?? item.bookingId,
                    amenityName: item.AmenityName ?? item.amenityName,
                    unitId: item.UnitId ?? item.unitId,
                    unitName: item.UnitName ?? item.unitName,
                    unitNumber: item.UnitNumber ?? item.unitNumber,
                    userName: item.UserName ?? item.userName,
                    bookingDate: item.BookingDate ?? item.bookingDate,
                    startTime: item.StartTime ?? item.startTime,
                    endTime: item.EndTime ?? item.endTime,
                    paidAmount: item.PaidAmount ?? item.paidAmount,
                    paymentStatus: item.PaymentStatus ?? item.paymentStatus,
                    bookingStatus: item.BookingStatus ?? item.bookingStatus,
                    guestCount: item.GuestCount ?? item.guestCount,
                }));
                setReports(mappedData.sort((a, b) => b.bookingId - a.bookingId));
            } else {
                setReports([]);
            }
        } catch (error) {
            toast.error('Failed to fetch reports.');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        if (filters.fromDate && filters.toDate && new Date(filters.fromDate) > new Date(filters.toDate)) {
            toast.error("From Date cannot be later than To Date");
            return;
        }
        fetchReports(filters.fromDate, filters.toDate, filters.bookingStatus);
    };

    // Calculate Summary Metrics
    const metrics = useMemo(() => {
        const total = reports.length;
        const approved = reports.filter(r => r.bookingStatus === 'Approved').length;
        const revenue = reports.reduce((sum, r) => sum + (r.paidAmount || 0), 0);
        return { total, approved, revenue };
    }, [reports]);

    const handleExport = () => {
        if (reports.length === 0) {
            toast.info("No data to export");
            return;
        }
        
        // Simple CSV Export logic
        const headers = ['Booking ID', 'Amenity Name', 'User Name', 'Date', 'Start Time', 'End Time', 'Guests', 'Status', 'Paid Amount', 'Payment Status'];
        const csvRows = [headers.join(',')];
        
        reports.forEach(r => {
            const row = [
                r.bookingId,
                `"${r.amenityName || ''}"`,
                `"${r.userName || ''}"`,
                r.bookingDate,
                r.startTime,
                r.endTime,
                r.guestCount || 0,
                r.bookingStatus,
                r.paidAmount || 0,
                r.paymentStatus || 'Unpaid'
            ];
            csvRows.push(row.join(','));
        });

        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `amenity_reports_${new Date().getTime()}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Report downloaded successfully");
    };

    const tableHeaders = [
        { label: "SR NO", key: "sno", align: "center" },
        { label: "AMENITY", key: "amenityName", align: "left" },
        { label: "BOOKED BY", key: "userName", align: "left" },
        { label: "DATE", key: "bookingDate", align: "center" },
        { label: "TIME", key: "time", align: "center" },
        { label: "STATUS", key: "bookingStatus", align: "center" },
        { label: "PAID AMT", key: "paidAmount", align: "center" },
        { label: "PAY STATUS", key: "paymentStatus", align: "center" },
    ];

    const getStatusStyle = (status) => {
        switch (status) {
            case 'Pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'Approved': return 'bg-green-100 text-green-800 border-green-200';
            case 'Rejected': return 'bg-red-100 text-red-800 border-red-200';
            case 'Cancelled': return 'bg-gray-100 text-gray-800 border-gray-200';
            default: return 'bg-gray-50 text-gray-600 border-gray-200';
        }
    };

    const renderCell = (key, row, header) => {
        switch (key) {
            case "sno":
                return <span className="font-semibold text-xs">{reports.indexOf(row) + 1}</span>;
            case "amenityName":
                return <span className="font-bold text-gray-800">{row.amenityName}</span>;
            case "userName":
                return (
                    <div className="flex flex-col">
                        <span className="text-sm font-semibold text-gray-700">{row.userName}</span>
                        {(row.unitName || row.unitNumber) && <span className="text-[10px] text-gray-400">Unit: {row.unitName || ''} {row.unitNumber ? `(${row.unitNumber})` : ''}</span>}
                    </div>
                );
            case "bookingDate":
                return <span className="text-xs font-medium text-gray-600">{row.bookingDate}</span>;
            case "time":
                return <span className="text-xs font-medium text-gray-600 whitespace-nowrap">{row.startTime?.substring(0,5)} - {row.endTime?.substring(0,5)}</span>;
            case "paidAmount":
                return <span className="text-xs font-bold text-gray-700">{row.paidAmount ? `₹${row.paidAmount}` : '-'}</span>;
            case "paymentStatus":
                return <span className={`text-[11px] uppercase font-bold ${row.paymentStatus === 'Paid' ? 'text-green-600' : (row.paymentStatus === 'Partial' ? 'text-yellow-600' : 'text-red-500')}`}>{row.paymentStatus || 'UNPAID'}</span>;
            case "bookingStatus":
                return (
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${getStatusStyle(row.bookingStatus)}`}>
                        {row.bookingStatus}
                    </span>
                );
            default:
                return row[key] || "-";
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="text-xl font-bold text-gray-800">Booking Reports</h3>
                    <p className="text-sm text-gray-500">Analyze amenity usage and revenue generation.</p>
                </div>
                <button
                    onClick={handleExport}
                    className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-indigo-600 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 shadow-sm"
                >
                    <FontAwesomeIcon icon={faDownload} />
                    Export CSV
                </button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-center gap-4 shadow-sm">
                    <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 text-xl">
                        <FontAwesomeIcon icon={faChartPie} />
                    </div>
                    <div>
                        <p className="text-sm text-gray-500 font-medium">Total Bookings</p>
                        <h4 className="text-2xl font-black text-gray-800">{metrics.total}</h4>
                    </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-center gap-4 shadow-sm">
                    <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center text-green-600 text-xl">
                        <FontAwesomeIcon icon={faCheckCircle} />
                    </div>
                    <div>
                        <p className="text-sm text-gray-500 font-medium">Approved Bookings</p>
                        <h4 className="text-2xl font-black text-gray-800">{metrics.approved}</h4>
                    </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-center gap-4 shadow-sm">
                    <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 text-xl">
                        <FontAwesomeIcon icon={faRupeeSign} />
                    </div>
                    <div>
                        <p className="text-sm text-gray-500 font-medium">Total Revenue</p>
                        <h4 className="text-2xl font-black text-gray-800">₹{metrics.revenue.toLocaleString()}</h4>
                    </div>
                </div>
            </div>

            {/* Filter Bar */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <form onSubmit={handleFilterSubmit} className="flex flex-col md:flex-row gap-4 items-end">
                    <div className="flex-1">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">From Date</label>
                        <input
                            type="date"
                            value={filters.fromDate}
                            onChange={(e) => setFilters({...filters, fromDate: e.target.value})}
                            className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-indigo-500 bg-white"
                        />
                    </div>
                    <div className="flex-1">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">To Date</label>
                        <input
                            type="date"
                            value={filters.toDate}
                            onChange={(e) => setFilters({...filters, toDate: e.target.value})}
                            className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-indigo-500 bg-white"
                        />
                    </div>
                    <div className="flex-1">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Booking Status</label>
                        <select
                            value={filters.bookingStatus}
                            onChange={(e) => setFilters({...filters, bookingStatus: e.target.value})}
                            className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-indigo-500 bg-white"
                        >
                            <option value="All">All Status</option>
                            <option value="Pending">Pending</option>
                            <option value="Approved">Approved</option>
                            <option value="Rejected">Rejected</option>
                            <option value="Cancelled">Cancelled</option>
                        </select>
                    </div>
                    <button
                        type="submit"
                        className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-6 rounded-lg transition-colors shadow-sm h-[38px] flex items-center justify-center shrink-0"
                    >
                        Generate Report
                    </button>
                </form>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                <CommonTable
                    loading={loading}
                    tableHeading="Report Data"
                    headers={tableHeaders}
                    data={reports}
                    renderCell={renderCell}
                    pagination={null}
                />
            </div>
        </div>
    );
};

export default AmenityReports;
